import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Shield, Building2, Users2, Link2, Activity,
  Plus, Copy, Check, Trash2, Calendar, AlertTriangle, RotateCw, DollarSign,
  Sparkles, Eye, Power, Clock, MapPin, X, Globe, Key, Lock, Unlock, Video
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE_CATEGORIES = [
  { key: 'camp', label: 'Summer Camps', short: 'Camps' },
  { key: 'afterschool', label: 'After-School Centres', short: 'After-School' },
  { key: 'language_centre', label: 'Language Centres', short: 'Lang. Centres' },
  { key: 'private_school', label: 'Private Schools', short: 'Private' },
  { key: 'public_school', label: 'Public Schools', short: 'Public' },
];

export function PlatformAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState('invites');

  if (user?.role !== 'admin') {
    return <div style={{ padding: '2rem' }}>Access denied.</div>;
  }

  const tabs = [
    { id: 'invites', label: 'Invite Links', icon: Sparkles },
    { id: 'demos', label: 'Demo Accounts', icon: Users2 },
    { id: 'orgs', label: 'Organizations', icon: Building2 },
    { id: 'links', label: 'Onboarding Links', icon: Link2 },
    { id: 'users', label: 'All Users', icon: Activity },
    { id: 'apikeys', label: 'API Keys', icon: Key },
    { id: 'walkthrough', label: 'Walkthrough Video', icon: Video },
  ];

  return (
    <div data-testid="platform-admin-page" style={{ height: '100%', overflow: 'auto', padding: '2rem 2.5rem', background: '#F8F9FB' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div style={{
          width: 36, height: 36, borderRadius: '0.5rem',
          background: '#1E2140', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Shield size={18} color="white" />
        </div>
        <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.75rem', fontWeight: 700, color: '#1A1D20', margin: 0 }}>
          Platform Admin
        </h1>
      </div>
      <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', color: '#64748B', marginBottom: '1.75rem' }}>
        Owner-only control panel. Manage demo accounts, organizations, onboarding links, and platform-wide activity.
      </p>

      {/* Tab bar */}
      <div data-testid="admin-tab-bar" style={{
        display: 'flex', gap: '0.25rem', borderBottom: '1.5px solid #E2E8F0', marginBottom: '1.5rem',
      }}>
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.75rem 1.25rem', background: 'transparent', border: 'none',
              borderBottom: tab === t.id ? '3px solid #1E2140' : '3px solid transparent',
              marginBottom: '-1.5px',
              fontFamily: 'Outfit, sans-serif', fontSize: '0.9rem',
              fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? '#1E2140' : '#64748B',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            <t.icon size={16} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'invites' && <InviteLinksTab />}
      {tab === 'demos' && <DemoAccountsTab />}
      {tab === 'orgs' && <OrganizationsTab />}
      {tab === 'links' && <SubscriptionLinksTab />}
      {tab === 'users' && <AllUsersTab />}
      {tab === 'apikeys' && <ApiKeysTab />}
      {tab === 'walkthrough' && <WalkthroughVideoTab />}
    </div>
  );
}

/* ================================================================ */
/*                    Walkthrough Video Tab                          */
/* ================================================================ */

function WalkthroughVideoTab() {
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedTick, setSavedTick] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/walkthrough-video`);
      setCurrent(r.data?.video || null);
      setInput(r.data?.video?.vimeo_url || '');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const push = async () => {
    if (saving) return;
    setError('');
    setSaving(true);
    try {
      const r = await axios.post(`${API_URL}/admin/walkthrough-video`, { vimeo_url: input.trim() });
      setCurrent(r.data?.video || null);
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (saving) return;
    if (!window.confirm('Remove the walkthrough video from all accounts?')) return;
    setError('');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/walkthrough-video`, { vimeo_url: '' });
      setCurrent(null);
      setInput('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Clear failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '1rem', fontFamily: 'Nunito, sans-serif', color: '#64748B' }}>Loading…</div>;

  return (
    <div data-testid="walkthrough-video-tab" style={{ maxWidth: 760 }}>
      <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.15rem', fontWeight: 700, color: '#1A1D20', margin: '0 0 0.4rem' }}>
        Quick Video Walkthrough
      </h2>
      <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', color: '#64748B', margin: '0 0 1.25rem', lineHeight: 1.55 }}>
        Paste any Vimeo link below and click <strong>Push to all accounts</strong>. The video appears at the top of every user's Guide page (right above Getting Started) and in the Guide sidebar as <em>Quick Video Walkthrough</em>. Replace or clear it at any time - changes are live instantly.
      </p>

      <label style={{ display: 'block', fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.4rem' }}>
        Vimeo URL
      </label>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <input
          data-testid="walkthrough-vimeo-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="https://vimeo.com/123456789 or https://vimeo.com/123456789/abcdef"
          style={{
            flex: 1, padding: '0.6rem 0.85rem',
            border: '1px solid #CBD5E1', borderRadius: '0.4rem',
            fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem',
            outline: 'none',
          }}
        />
        <button
          data-testid="walkthrough-push-btn"
          onClick={push}
          disabled={saving || !input.trim()}
          style={{
            padding: '0.6rem 1.1rem', borderRadius: '0.4rem',
            border: 'none',
            background: (saving || !input.trim()) ? '#94A3B8' : '#1E2140',
            color: 'white',
            fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: '0.85rem',
            cursor: (saving || !input.trim()) ? 'wait' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {saving ? 'Saving…' : (savedTick ? 'Pushed ✓' : 'Push to all accounts')}
        </button>
      </div>
      {error && (
        <div data-testid="walkthrough-error" style={{
          fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', color: '#B91C1C',
          background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.35rem',
          padding: '0.5rem 0.7rem', marginBottom: '0.5rem',
        }}>
          {error}
        </div>
      )}

      {current?.embed_url ? (
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', fontWeight: 600, color: '#16A34A' }}>
              ● Live for all accounts
            </span>
            <button
              data-testid="walkthrough-clear-btn"
              onClick={clear}
              disabled={saving}
              style={{
                padding: '0.35rem 0.75rem', borderRadius: '0.35rem',
                border: '1px solid #FCA5A5', background: 'white',
                fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', fontWeight: 600,
                color: '#B91C1C', cursor: 'pointer',
              }}
            >
              Remove video
            </button>
          </div>
          <div
            data-testid="walkthrough-admin-preview"
            style={{
              borderRadius: '8px', overflow: 'hidden', border: '1px solid #E2E8F0',
              background: '#000', aspectRatio: '16 / 9',
            }}
          >
            <iframe
              src={current.embed_url}
              title="Walkthrough preview"
              width="100%" height="100%"
              frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
              style={{ display: 'block', border: 0 }}
            />
          </div>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#94A3B8', marginTop: '0.5rem' }}>
            Source: <code style={{ fontSize: '0.75rem' }}>{current.vimeo_url}</code>
            {current.updated_at && (
              <span> · updated {new Date(current.updated_at).toLocaleString()}</span>
            )}
          </p>
        </div>
      ) : (
        <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', color: '#94A3B8', fontStyle: 'italic', marginTop: '1rem' }}>
          No video set. The Guide page will hide the Walkthrough section until one is pushed.
        </p>
      )}
    </div>
  );
}

/* ================================================================ */
/*                       Invite Links                                 */
/* ================================================================ */

function InviteLinksTab() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [tz, setTz] = useState(() => {
    try { return localStorage.getItem('admin_tz') || 'America/Vancouver'; } catch { return 'America/Vancouver'; }
  });

  const changeTz = (v) => {
    setTz(v);
    try { localStorage.setItem('admin_tz', v); } catch { /* noop */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/invites`);
      setLinks(data);
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="invite-links-tab">
      <div style={{
        background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
        padding: '1.5rem', marginBottom: '1.25rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem', fontWeight: 600, color: '#1A1D20', margin: 0, marginBottom: '0.375rem' }}>
              Invite links
            </h3>
            <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', margin: 0, lineHeight: 1.6 }}>
              Generic, short links you email to leads. Anyone who clicks lands directly inside the app with no login or signup. Each click is tracked: distinct devices, time spent, games tried, return visits.
            </p>
          </div>
          <button
            data-testid="new-invites-btn"
            onClick={() => setShowCreate(!showCreate)}
            style={primaryBtn}
          >
            <Plus size={16} /> {showCreate ? 'Cancel' : 'Generate links'}
          </button>
        </div>

        <div style={{ marginTop: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', color: '#94A3B8', fontWeight: 600 }}>Times shown in:</span>
          <TimezonePicker value={tz} onChange={changeTz} />
        </div>

        {showCreate && <BulkInviteForm onCreated={() => { setShowCreate(false); load(); }} />}
      </div>

      {loading ? <Loading /> : links.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="No invite links yet"
          subtitle="Generate a batch and paste the URLs into your email sequence. Each link works on any device and accumulates intel as people use it."
        />
      ) : (
        <div data-testid="invite-links-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {links.map(l => <InviteLinkRow key={l.id} link={l} tz={tz} onOpen={() => setDetailId(l.id)} onChange={load} />)}
        </div>
      )}

      {detailId && (
        <InviteDetailDrawer id={detailId} tz={tz} onTzChange={changeTz} onClose={() => setDetailId(null)} onChange={load} />
      )}
    </div>
  );
}

function BulkInviteForm({ onCreated }) {
  const [count, setCount] = useState(10);
  const [duration, setDuration] = useState(30);
  const [label, setLabel] = useState('');
  const [accountType, setAccountType] = useState('school');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [justCreated, setJustCreated] = useState(null);

  const submit = async () => {
    setMsg('');
    const n = Number(count);
    if (!n || n < 1) { setMsg('Enter how many links to generate.'); return; }
    if (n > 500) { setMsg('Max 500 links per batch.'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/invites`, {
        count: n,
        default_duration_days: Number(duration),
        label,
        account_type: accountType,
      });
      setJustCreated(data.links);
      setTimeout(() => onCreated(), 50);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Could not generate links.');
    }
    setSaving(false);
  };

  const copyAll = () => {
    if (!justCreated) return;
    const urls = justCreated.map(l => `${window.location.origin}/try/${l.slug}`).join('\n');
    navigator.clipboard.writeText(urls);
  };

  return (
    <div data-testid="bulk-invite-form" style={{ borderTop: '1px solid #F1F5F9', paddingTop: '1.25rem', marginTop: '1.25rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '120px 140px 200px 1fr', gap: '0.75rem' }}>
        <Field label="How many">
          <input type="number" min="1" max="500" value={count} onChange={e => setCount(e.target.value)} data-testid="invites-count-input" style={inputStyle} />
        </Field>
        <Field label="Days of access (from first click)">
          <input type="number" min="1" max="365" value={duration} onChange={e => setDuration(e.target.value)} data-testid="invites-duration-input" style={inputStyle} />
        </Field>
        <Field label="Account type">
          <select
            value={accountType}
            onChange={e => setAccountType(e.target.value)}
            data-testid="invites-account-type-select"
            style={inputStyle}
          >
            <option value="school">School</option>
            <option value="private_center">Private Center</option>
          </select>
        </Field>
        <Field label="Internal note (optional)">
          <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g., batch jan-15 schools" data-testid="invites-label-input" style={inputStyle} />
        </Field>
      </div>
      <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button onClick={submit} disabled={saving} data-testid="invites-create-submit" style={primaryBtn}>
          {saving ? 'Generating...' : `Generate ${count} link${Number(count) === 1 ? '' : 's'}`}
        </button>
        {justCreated && justCreated.length > 0 && (
          <button onClick={copyAll} data-testid="invites-copy-all-just-created" style={{ ...primaryBtn, background: '#2F9E44' }}>
            <Copy size={14} /> Copy all {justCreated.length} URLs
          </button>
        )}
        {msg && <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#E03131' }}>{msg}</span>}
      </div>
    </div>
  );
}

function InviteLinkRow({ link, tz, onOpen, onChange }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/try/${link.slug}`;

  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const togglePower = async (e) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API_URL}/admin/invites/${link.id}`, { is_active: !link.is_active });
      onChange();
    } catch { /* noop */ }
  };

  const toggleAccountType = async (e) => {
    e.stopPropagation();
    const next = link.account_type === 'private_center' ? 'school' : 'private_center';
    try {
      await axios.patch(`${API_URL}/admin/invites/${link.id}`, { account_type: next });
      onChange();
    } catch { /* noop */ }
  };

  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Permanently delete /try/${link.slug}? This also removes every visitor's session and intel collected from this link. Use Power-off (revoke) if you want to stop new visitors but keep the data.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/invites/${link.id}`);
      onChange();
    } catch { /* noop */ }
  };

  return (
    <div
      data-testid={`invite-row-${link.id}`}
      onClick={onOpen}
      style={{
        background: 'white', borderRadius: '0.625rem', border: '1px solid #E2E8F0',
        padding: '0.875rem 1.125rem', display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto',
        gap: '1rem', alignItems: 'center', cursor: 'pointer',
        opacity: link.is_active ? 1 : 0.55,
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: '0.5rem',
        background: link.is_active ? '#EDF2FF' : '#F1F5F9',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Sparkles size={15} color={link.is_active ? '#4C6EF5' : '#94A3B8'} />
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem', color: '#1A1D20' }}>
            /try/{link.slug}
          </span>
          {!link.is_active && <span style={{ ...badge, background: '#FEF2F2', color: '#DC2626' }}>Revoked</span>}
          {link.label && <span style={{ ...badge, background: '#F1F5F9', color: '#475569' }}>{link.label}</span>}
          <span style={{ ...badge, background: '#EBFBEE', color: '#2F9E44' }}>{link.default_duration_days}d access</span>
          <button
            data-testid={`invite-account-type-${link.id}`}
            onClick={toggleAccountType}
            title="Click to flip between School and Private Center"
            style={{
              ...badge,
              background: link.account_type === 'private_center' ? '#FEF3C7' : '#DBEAFE',
              color: link.account_type === 'private_center' ? '#92400E' : '#1E3A8A',
              border: 'none', cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
            }}
          >
            {link.account_type === 'private_center' ? 'Private Center' : 'School'}
          </button>
        </div>
        <div style={{ marginTop: '0.375rem', display: 'flex', gap: '1.25rem', flexWrap: 'wrap', fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', color: '#64748B' }}>
          <span><strong style={{ color: '#1A1D20' }}>{link.use_count || 0}</strong> click{link.use_count === 1 ? '' : 's'}</span>
          <span><strong style={{ color: '#1A1D20' }}>{link.visitor_count || 0}</strong> visitor{link.visitor_count === 1 ? '' : 's'}</span>
          <span><strong style={{ color: '#1A1D20' }}>{formatDuration(link.total_time_seconds || 0)}</strong> total time</span>
          <span><strong style={{ color: '#1A1D20' }}>{link.events_count || 0}</strong> events</span>
          {link.last_active_at && <span>last activity {formatDateTime(link.last_active_at, tz)}</span>}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={e => e.stopPropagation()}>
        <button data-testid={`invite-copy-${link.id}`} onClick={copy} title="Copy URL" style={iconBtn}>
          {copied ? <Check size={15} color="#2F9E44" /> : <Copy size={15} />}
        </button>
        <button data-testid={`invite-toggle-${link.id}`} onClick={togglePower} title={link.is_active ? 'Revoke (stop new visitors)' : 'Reactivate'} style={iconBtn}>
          <Power size={15} color={link.is_active ? '#2F9E44' : '#94A3B8'} />
        </button>
        <button data-testid={`invite-delete-${link.id}`} onClick={remove} title="Delete (also wipes intel)" style={{ ...iconBtn, color: '#E03131' }}>
          <Trash2 size={15} />
        </button>
      </div>

      <button data-testid={`invite-open-${link.id}`} onClick={(e) => { e.stopPropagation(); onOpen(); }} style={{ ...iconBtn, color: '#1E2140' }} title="View intel">
        <Eye size={15} />
      </button>
    </div>
  );
}

function InviteDetailDrawer({ id, tz, onTzChange, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extendDays, setExtendDays] = useState(15);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/invites/${id}`);
      setData(data);
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const extendAll = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API_URL}/admin/invites/${id}/extend-all`, { days: Number(extendDays) });
      await load();
      onChange();
    } catch { /* noop */ }
    setBusy(false);
  };

  return (
    <div data-testid="invite-detail-overlay" style={drawerOverlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div data-testid="invite-detail-drawer" style={drawer}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '1.05rem', color: '#1A1D20' }}>
              {data ? `/try/${data.slug}` : 'Loading…'}
            </div>
            {data && (
              <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#64748B', marginTop: '0.25rem' }}>
                {data.label || 'No label'} · {data.default_duration_days}d default access · created {formatDateTime(data.created_at, tz)}
              </div>
            )}
          </div>
          <button data-testid="invite-detail-close" onClick={onClose} style={iconBtn} title="Close">
            <X size={16} />
          </button>
        </div>

        {loading || !data ? <Loading /> : (
          <div style={{ padding: '1.25rem 1.5rem', overflow: 'auto', flex: 1 }}>
            {/* Timezone picker inside drawer */}
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', color: '#94A3B8', fontWeight: 600 }}>Times shown in:</span>
              <TimezonePicker value={tz} onChange={onTzChange} />
            </div>

            {/* Summary stats */}
            <div data-testid="invite-detail-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <StatTile label="Clicks" value={data.use_count} />
              <StatTile label="Distinct visitors" value={data.visitor_count} />
              <StatTile label="Total time" value={formatDuration(data.total_time_seconds)} />
              <StatTile label="Events" value={data.events_count} />
            </div>

            {/* Bulk extend */}
            <div style={{
              background: '#F8F9FB', borderRadius: '0.625rem', padding: '0.875rem 1rem',
              marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
            }}>
              <Clock size={16} color="#64748B" />
              <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>
                Extend every visitor by
              </span>
              <input
                type="number" min="1" max="365" value={extendDays}
                onChange={e => setExtendDays(e.target.value)}
                data-testid="invite-extend-days-input"
                style={{ ...inputStyle, width: 80 }}
              />
              <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#64748B' }}>days</span>
              <button data-testid="invite-extend-submit" disabled={busy} onClick={extendAll} style={{ ...primaryBtn, padding: '0.5rem 0.875rem' }}>
                {busy ? 'Extending...' : 'Apply'}
              </button>
            </div>

            {/* Visitors */}
            <h4 style={sectionH}>Visitors ({data.visitors.length})</h4>
            {data.visitors.length === 0 ? (
              <p style={emptyText}>No visitors yet. Once someone clicks the link, they'll appear here.</p>
            ) : (
              <div data-testid="invite-visitors-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                {data.visitors.map(v => (
                  <div key={v.user_id} data-testid={`visitor-${v.user_id}`} style={{
                    border: '1px solid #E2E8F0', borderRadius: '0.5rem',
                    padding: '0.75rem 1rem', background: 'white',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#475569', fontWeight: 600 }}>
                            visitor #{v.user_id.slice(-6)}
                          </span>
                          {v.onboarding_completed && <span style={{ ...badge, background: '#EBFBEE', color: '#2F9E44' }}>Onboarded</span>}
                        </div>
                        <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#94A3B8', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                          <span>{formatDuration(v.total_time_seconds)} active</span>
                          <span>{v.visit_count} return{v.visit_count === 1 ? '' : 's'}</span>
                          <span>{v.events_count} events</span>
                          <span>{v.days_active} day{v.days_active === 1 ? '' : 's'}</span>
                          {v.expires_at && <span>expires {new Date(v.expires_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</span>}
                        </div>
                        {v.user_agent && (
                          <div style={{ marginTop: '0.25rem', fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', color: '#94A3B8' }}>
                            <MapPin size={10} style={{ display: 'inline', marginRight: '0.25rem' }} />
                            {compactUA(v.user_agent)}{v.ip ? ` · ${v.ip}` : ''}
                          </div>
                        )}
                      </div>
                      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', color: '#64748B', textAlign: 'right', flexShrink: 0 }}>
                        first seen {formatDateTime(v.first_seen_at, tz)}<br />
                        last active {formatDateTime(v.last_active_at, tz)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Activity timeline */}
            <h4 style={sectionH}>Recent activity (last {data.events.length})</h4>
            {data.events.length === 0 ? (
              <p style={emptyText}>No activity yet.</p>
            ) : (
              <div data-testid="invite-events-list" style={{ borderLeft: '2px solid #E2E8F0', paddingLeft: '0.875rem', marginLeft: '0.25rem' }}>
                {data.events.map((ev, i) => (
                  <div key={i} style={{ position: 'relative', paddingBottom: '0.625rem' }}>
                    <span style={{
                      position: 'absolute', left: -19, top: 4,
                      width: 8, height: 8, borderRadius: '50%',
                      background: eventColor(ev.event_type),
                      border: '2px solid white',
                    }} />
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', color: '#1A1D20' }}>
                      <strong>{prettyEventLabel(ev.event_type)}</strong>
                      {ev.event_data && Object.keys(ev.event_data).length > 0 && (
                        <span style={{ color: '#64748B' }}> · {summarizeEventData(ev.event_data)}</span>
                      )}
                    </div>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', color: '#94A3B8' }}>
                      {ev.user_id ? `visitor #${ev.user_id.slice(-6)} · ` : ''}{formatDateTime(ev.ts, tz)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div style={{
      background: 'white', border: '1px solid #E2E8F0', borderRadius: '0.625rem',
      padding: '0.875rem 1rem',
    }}>
      <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.5rem', fontWeight: 700, color: '#1A1D20', lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', color: '#64748B', marginTop: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
        {label}
      </div>
    </div>
  );
}

function formatDuration(seconds) {
  const s = Math.max(0, Number(seconds || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}

const TIMEZONE_OPTIONS = [
  { value: 'America/Vancouver', label: 'Vancouver (Pacific)' },
  { value: 'America/Edmonton', label: 'Edmonton (Mountain)' },
  { value: 'America/Winnipeg', label: 'Winnipeg (Central)' },
  { value: 'America/Toronto', label: 'Toronto / Ontario (Eastern)' },
  { value: 'America/Halifax', label: 'Halifax (Atlantic)' },
  { value: 'America/St_Johns', label: "St. John's (Newfoundland)" },
  { value: 'UTC', label: 'UTC' },
];

function formatDateTime(iso, tz) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-CA', {
      timeZone: tz || 'America/Vancouver',
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return iso;
  }
}

function TimezonePicker({ value, onChange }) {
  return (
    <div data-testid="timezone-picker" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
      <Globe size={14} color="#64748B" />
      <select
        data-testid="timezone-select"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', color: '#475569',
          border: '1.5px solid #E2E8F0', borderRadius: '0.375rem',
          padding: '0.3rem 0.5rem', background: 'white', cursor: 'pointer', outline: 'none',
        }}
      >
        {TIMEZONE_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function compactUA(ua) {
  if (!ua) return '';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Linux/i.test(ua)) return 'Linux';
  return ua.slice(0, 40);
}

function eventColor(t) {
  if (t === 'first_visit') return '#4C6EF5';
  if (t === 'return_visit') return '#7950F2';
  if (t === 'onboarding_completed') return '#2F9E44';
  if (t === 'game_started' || t === 'game_completed') return '#F59F00';
  if (t === 'pdf_downloaded') return '#E8590C';
  return '#94A3B8';
}

function prettyEventLabel(t) {
  const map = {
    first_visit: 'First visit',
    return_visit: 'Return visit',
    page_view: 'Page view',
    onboarding_completed: 'Finished onboarding',
    game_started: 'Started a game',
    game_completed: 'Completed a game',
    pdf_downloaded: 'Downloaded a PDF',
  };
  return map[t] || t.replace(/_/g, ' ');
}

function summarizeEventData(d) {
  if (!d || typeof d !== 'object') return '';
  if (d.path) return d.path;
  if (d.game) return `${d.game}${d.mode ? ` · ${d.mode}` : ''}`;
  if (d.report_type) return d.report_type;
  const keys = Object.keys(d).slice(0, 2);
  return keys.map(k => `${k}=${typeof d[k] === 'string' ? d[k].slice(0, 30) : JSON.stringify(d[k]).slice(0, 30)}`).join(', ');
}

const sectionH = {
  fontFamily: 'Outfit, sans-serif', fontSize: '0.85rem', fontWeight: 700,
  color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em',
  margin: '0 0 0.625rem',
};
const emptyText = {
  fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#94A3B8',
  marginBottom: '1.5rem',
};
const drawerOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(15, 19, 35, 0.4)',
  display: 'flex', justifyContent: 'flex-end', zIndex: 9000,
};
const drawer = {
  width: '100%', maxWidth: 720, background: 'white',
  display: 'flex', flexDirection: 'column', height: '100%',
  boxShadow: '-12px 0 32px rgba(0,0,0,0.15)',
};

/* ================================================================ */
/*                       Demo Accounts                                */
/* ================================================================ */

function DemoAccountsTab() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/demo-accounts`);
      setAccounts(data);
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="demo-accounts-tab">
      <div style={{
        background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
        padding: '1.5rem', marginBottom: '1.25rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showForm ? '1.25rem' : 0 }}>
          <div>
            <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem', fontWeight: 600, color: '#1A1D20', margin: 0, marginBottom: '0.25rem' }}>
              {accounts.length} demo {accounts.length === 1 ? 'account' : 'accounts'}
            </h3>
            <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', margin: 0 }}>
              Pre-created accounts with credentials you set. Free for the duration you choose. Extend or revive anytime.
            </p>
          </div>
          <button
            data-testid="new-demo-btn"
            onClick={() => setShowForm(!showForm)}
            style={primaryBtn}
          >
            <Plus size={16} /> {showForm ? 'Cancel' : 'New demo account'}
          </button>
        </div>

        {showForm && <NewDemoForm onCreated={() => { setShowForm(false); load(); }} />}
      </div>

      {loading ? <Loading /> : accounts.length === 0 ? (
        <EmptyState
          icon={Users2}
          title="No demo accounts yet"
          subtitle="Create your first demo account to email credentials to a prospect."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {accounts.map(a => <DemoAccountCard key={a._id} account={a} onChange={load} />)}
        </div>
      )}
    </div>
  );
}

function NewDemoForm({ onCreated }) {
  const [form, setForm] = useState({
    name: '', email: '', password: '', school_name: '', label: '',
    expires_in_days: 15, programs_enabled: true, monthly_amount: '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setMsg('');
    if (!form.name || !form.email || !form.password) {
      setMsg('Name, email, and password are required.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/demo-accounts`, {
        ...form,
        expires_in_days: Number(form.expires_in_days),
        monthly_amount: form.monthly_amount ? Number(form.monthly_amount) : null,
      });
      onCreated();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Could not create demo account.');
    }
    setSaving(false);
  };

  const durationOptions = [7, 15, 30];

  return (
    <div data-testid="new-demo-form" style={{ borderTop: '1px solid #F1F5F9', paddingTop: '1.25rem', marginTop: '1.25rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <Field label="Display name (e.g., Sir Pro)" testId="demo-name">
          <input value={form.name} onChange={e => update('name', e.target.value)} data-testid="demo-name-input" style={inputStyle} />
        </Field>
        <Field label="Email">
          <input type="email" value={form.email} onChange={e => update('email', e.target.value)} data-testid="demo-email-input" style={inputStyle} />
        </Field>
        <Field label="Password (you set it)">
          <input value={form.password} onChange={e => update('password', e.target.value)} data-testid="demo-password-input" style={inputStyle} />
        </Field>
        <Field label="School / institution (optional)">
          <input value={form.school_name} onChange={e => update('school_name', e.target.value)} data-testid="demo-school-input" style={inputStyle} />
        </Field>
        <Field label="Internal label (your note)">
          <input value={form.label} onChange={e => update('label', e.target.value)} placeholder="e.g., Toronto DSB lead, June outreach" data-testid="demo-label-input" style={inputStyle} />
        </Field>
        <Field label="Future monthly price (informational, optional)">
          <input type="number" min="0" value={form.monthly_amount} onChange={e => update('monthly_amount', e.target.value)} placeholder="e.g., 250" data-testid="demo-monthly-input" style={inputStyle} />
        </Field>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <label style={smallLabel}>Demo duration</label>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.375rem' }}>
          {durationOptions.map(d => (
            <button key={d} type="button" onClick={() => update('expires_in_days', d)} data-testid={`demo-days-${d}`} style={pill(form.expires_in_days === d)}>
              {d} days
            </button>
          ))}
          <input
            type="number" min="1" max="365"
            value={form.expires_in_days}
            onChange={e => update('expires_in_days', e.target.value)}
            data-testid="demo-days-custom"
            style={{ ...inputStyle, width: 100 }}
          />
        </div>
      </div>

      <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
        <input
          type="checkbox" id="programs-on" data-testid="demo-programs-toggle"
          checked={form.programs_enabled} onChange={e => update('programs_enabled', e.target.checked)}
          style={{ width: 16, height: 16, cursor: 'pointer' }}
        />
        <label htmlFor="programs-on" style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#1A1D20', cursor: 'pointer' }}>
          Enable the Programs section for this demo
        </label>
      </div>

      <div style={{ marginTop: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button onClick={submit} disabled={saving} data-testid="demo-create-submit" style={primaryBtn}>
          {saving ? 'Creating...' : 'Create demo account'}
        </button>
        {msg && <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#E03131' }}>{msg}</span>}
      </div>
    </div>
  );
}

function DemoAccountCard({ account, onChange }) {
  const [copied, setCopied] = useState(null);
  const [extending, setExtending] = useState(false);
  const [showExtend, setShowExtend] = useState(false);

  const expired = account.is_expired;
  const daysLeft = account.days_remaining;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const extend = async (days) => {
    setExtending(true);
    try {
      await axios.patch(`${API_URL}/admin/demo-accounts/${account._id}/extend`, { days });
      setShowExtend(false);
      onChange();
    } catch { /* noop */ }
    setExtending(false);
  };

  const remove = async () => {
    if (!window.confirm(`Permanently remove demo account "${account.name}" (${account.email})? This deletes their sessions, playlists, and classrooms too.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/demo-accounts/${account._id}`);
      onChange();
    } catch { /* noop */ }
  };

  return (
    <div data-testid={`demo-card-${account._id}`} style={{
      background: 'white', borderRadius: '0.75rem', border: '1px solid #E2E8F0',
      padding: '1.125rem 1.25rem', opacity: expired ? 0.85 : 1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.375rem' }}>
            <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1A1D20' }}>
              {account.name}
            </span>
            {expired ? (
              <span style={{ ...badge, background: '#FEF2F2', color: '#DC2626' }}>
                <AlertTriangle size={11} /> Expired {Math.abs(daysLeft)}d ago
              </span>
            ) : (
              <span style={{ ...badge, background: daysLeft <= 3 ? '#FFFBEB' : '#EBFBEE', color: daysLeft <= 3 ? '#D97706' : '#2F9E44' }}>
                <Calendar size={11} /> {daysLeft}d left
              </span>
            )}
            {account.demo_label && (
              <span style={{ ...badge, background: '#F1F5F9', color: '#475569' }}>
                {account.demo_label}
              </span>
            )}
            {account.programs_enabled && (
              <span style={{ ...badge, background: '#EDF2FF', color: '#4C6EF5' }}>Programs ON</span>
            )}
          </div>

          <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', color: '#64748B', marginBottom: '0.25rem' }}>
            {account.email}{account.school_name ? ` · ${account.school_name}` : ''}
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem', fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#94A3B8' }}>
            <span>{account.total_sessions} session{account.total_sessions !== 1 ? 's' : ''}</span>
            {account.unique_games > 0 && <span>{account.unique_games} game{account.unique_games !== 1 ? 's' : ''}</span>}
            <span>Last active: {account.last_active ? new Date(account.last_active).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : 'Never logged in'}</span>
            {account.demo_extensions > 0 && <span>Extended {account.demo_extensions}x</span>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
          <button
            data-testid={`demo-copy-${account._id}`}
            onClick={() => copy(`Email: ${account.email}\nPassword: (the password you set when creating this demo)`, 'creds')}
            title="Copy email"
            style={iconBtn}
          >
            {copied === 'creds' ? <Check size={15} color="#2F9E44" /> : <Copy size={15} />}
          </button>
          <button
            data-testid={`demo-extend-${account._id}`}
            onClick={() => setShowExtend(!showExtend)}
            title="Extend demo"
            style={iconBtn}
          >
            <RotateCw size={15} />
          </button>
          <button
            data-testid={`demo-delete-${account._id}`}
            onClick={remove}
            title="Remove demo account"
            style={{ ...iconBtn, color: '#E03131' }}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {showExtend && (
        <div data-testid={`demo-extend-panel-${account._id}`} style={{
          marginTop: '0.875rem', paddingTop: '0.875rem', borderTop: '1px dashed #E2E8F0',
          display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
        }}>
          <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
            Extend by:
          </span>
          {[7, 15, 30].map(d => (
            <button
              key={d}
              data-testid={`demo-extend-${account._id}-${d}`}
              disabled={extending}
              onClick={() => extend(d)}
              style={pill(false)}
            >
              +{d} days
            </button>
          ))}
          <CustomExtendInput onSubmit={extend} disabled={extending} />
        </div>
      )}
    </div>
  );
}

function CustomExtendInput({ onSubmit, disabled }) {
  const [val, setVal] = useState('');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
      <input
        type="number" min="1" max="365"
        value={val}
        onChange={e => setVal(e.target.value)}
        placeholder="custom"
        style={{ ...inputStyle, width: 80, padding: '0.4rem 0.625rem' }}
      />
      <button
        disabled={disabled || !val}
        onClick={() => { onSubmit(Number(val)); setVal(''); }}
        style={{ ...pill(true), opacity: !val ? 0.5 : 1 }}
      >
        Apply
      </button>
    </div>
  );
}

/* ================================================================ */
/*                       Organizations                                */
/* ================================================================ */

function OrganizationsTab() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/admin/organizations`)
      .then(r => setOrgs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (orgs.length === 0) {
    return <EmptyState icon={Building2} title="No organizations yet" subtitle="Organizations appear when teachers sign up or you create demo accounts with a school name." />;
  }

  return (
    <div data-testid="organizations-tab" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {orgs.map((org, i) => {
        const isOpen = expanded === i;
        return (
          <div key={i} data-testid={`org-card-${i}`} style={{
            background: 'white', borderRadius: '0.75rem', border: '1px solid #E2E8F0',
          }}>
            <div
              onClick={() => setExpanded(isOpen ? null : i)}
              style={{ padding: '1.125rem 1.25rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '1rem' }}
            >
              <Building2 size={20} color="#1E2140" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1A1D20' }}>
                  {org.school_name}
                </div>
                <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#64748B', marginTop: '0.125rem' }}>
                  {org.center_admin_count + org.teacher_count} {org.center_admin_count + org.teacher_count === 1 ? 'member' : 'members'}
                  {org.demo_count > 0 && ` · ${org.demo_count} demo`}
                  {org.active_subscription_count > 0 && ` · ${org.active_subscription_count} subscribed`}
                  {` · ${org.total_sessions} sessions total`}
                  {org.last_active && ` · last active ${new Date(org.last_active).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}`}
                </div>
              </div>
            </div>
            {isOpen && (
              <div data-testid={`org-members-${i}`} style={{ borderTop: '1px solid #F1F5F9', padding: '0.75rem 1.25rem' }}>
                {org.members.map(m => (
                  <div key={m.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '0.5rem 0', borderBottom: '1px solid #F8F9FB', fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem',
                  }}>
                    <div>
                      <span style={{ fontWeight: 600, color: '#1A1D20' }}>{m.name || '(no name)'}</span>
                      <span style={{ color: '#94A3B8', marginLeft: '0.5rem' }}>{m.email}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: '#64748B' }}>
                      <span style={{ ...badge, background: statusBg(m.status), color: statusColor(m.status) }}>{m.status}</span>
                      <span>{m.sessions} sess.</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ================================================================ */
/*                  Subscription Links (relocated)                   */
/* ================================================================ */

function SubscriptionLinksTab() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    amount: '', currency: 'cad', label: '',
    trial_days: 15, expires_in_days: 30, max_uses: 1,
  });
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/billing/pricing-links`);
      setLinks(data);
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.amount || Number(form.amount) <= 0) { setMsg('Enter a valid monthly amount.'); return; }
    setMsg('');
    try {
      await axios.post(`${API_URL}/billing/pricing-links`, {
        amount: Number(form.amount),
        currency: form.currency,
        label: form.label,
        trial_days: Number(form.trial_days),
        expires_in_days: Number(form.expires_in_days),
        max_uses: Number(form.max_uses),
      });
      setForm({ ...form, amount: '', label: '' });
      setMsg('Link created');
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Error'); }
  };

  const remove = async (token) => {
    try { await axios.delete(`${API_URL}/billing/pricing-links/${token}`); load(); } catch { /* noop */ }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/subscribe/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(null), 2500);
  };

  return (
    <div data-testid="subscription-links-tab">
      <div style={{
        background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0', padding: '1.5rem', marginBottom: '1.25rem',
      }}>
        <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem', fontWeight: 600, color: '#1A1D20', margin: 0, marginBottom: '0.25rem' }}>
          Onboarding links
        </h3>
        <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', margin: 0, marginBottom: '1.25rem' }}>
          Email these links to schools and centres. Each link sets the monthly subscription amount, trial duration, and how many people can use it. Onboarded users become Center Admins and can add their own sub-teachers immediately.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '140px 100px 1fr', gap: '0.625rem', marginBottom: '0.75rem' }}>
          <Field label="Monthly price">
            <input value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="link-amount-input" style={inputStyle} placeholder="250" />
          </Field>
          <Field label="Currency">
            <input value={form.currency} onChange={e => setForm({...form, currency: e.target.value})} data-testid="link-currency-input" style={inputStyle} />
          </Field>
          <Field label="Label (optional)">
            <input value={form.label} onChange={e => setForm({...form, label: e.target.value})} data-testid="link-label-input" style={inputStyle} placeholder="e.g., Toronto DSB" />
          </Field>
        </div>

        <PillRow label="Trial period" value={form.trial_days} onChange={v => setForm({...form, trial_days: v})} options={[
          { v: 0, l: 'No trial' }, { v: 15, l: '15 days' }, { v: 20, l: '20 days' }, { v: 30, l: '30 days' },
        ]} testIdPrefix="link-trial" />

        <PillRow label="Link expires after" value={form.expires_in_days} onChange={v => setForm({...form, expires_in_days: v})} options={[
          { v: 0, l: 'Never' }, { v: 7, l: '7 days' }, { v: 30, l: '30 days' }, { v: 90, l: '90 days' },
        ]} testIdPrefix="link-expiry" />

        <PillRow label="Usage limit" value={form.max_uses} onChange={v => setForm({...form, max_uses: v})} options={[
          { v: 1, l: 'Single use' }, { v: 5, l: '5 uses' }, { v: 10, l: '10 uses' }, { v: 0, l: 'Unlimited' },
        ]} testIdPrefix="link-max-uses" />

        <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={create} data-testid="link-create-submit" style={primaryBtn}>
            <Plus size={16} /> Generate link
          </button>
          {msg && <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: msg.includes('created') ? '#2F9E44' : '#E03131' }}>{msg}</span>}
        </div>
      </div>

      {loading ? <Loading /> : links.length === 0 ? null : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {links.map((link, i) => {
            const expired = link.expires_at && new Date(link.expires_at) < new Date();
            const used = (link.max_uses ?? 1) > 0 && (link.use_count ?? 0) >= (link.max_uses ?? 1);
            const inactive = expired || used;
            return (
              <div key={link.token} data-testid={`link-card-${i}`} style={{
                background: 'white', borderRadius: '0.625rem', border: '1px solid #E2E8F0',
                padding: '0.875rem 1.125rem', display: 'flex', alignItems: 'center', gap: '1rem',
                opacity: inactive ? 0.55 : 1,
              }}>
                <DollarSign size={18} color="#1E2140" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700, fontSize: '1rem' }}>${link.amount}</span>
                    <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#94A3B8' }}>{(link.currency || 'cad').toUpperCase()}/mo</span>
                    {link.trial_days > 0 && <span style={{ ...badge, background: '#EDF2FF', color: '#4C6EF5' }}>{link.trial_days}d trial</span>}
                    {expired && <span style={{ ...badge, background: '#FEF2F2', color: '#DC2626' }}>Expired</span>}
                    {used && !expired && <span style={{ ...badge, background: '#FFFBEB', color: '#D97706' }}>Fully used</span>}
                    {link.label && <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#64748B' }}>{link.label}</span>}
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#94A3B8', marginTop: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {window.location.origin}/subscribe/{link.token}
                  </div>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', color: '#64748B', marginTop: '0.25rem' }}>
                    {link.use_count || 0} / {link.max_uses === 0 ? '∞' : link.max_uses} used
                    {link.expires_at && !expired && ` · expires ${new Date(link.expires_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}`}
                  </div>
                </div>
                {!inactive && (
                  <button data-testid={`link-copy-${i}`} onClick={() => copyLink(link.token)} style={iconBtn}>
                    {copied === link.token ? <Check size={15} color="#2F9E44" /> : <Copy size={15} />}
                  </button>
                )}
                <button data-testid={`link-delete-${i}`} onClick={() => remove(link.token)} style={{ ...iconBtn, color: '#E03131' }}>
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================================================================ */
/*                          All Users                                 */
/* ================================================================ */

function AllUsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCategoriesFor, setOpenCategoriesFor] = useState(null); // user._id
  const [resourceModalFor, setResourceModalFor] = useState(null); // user._id
  const [resourceCatalogue, setResourceCatalogue] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/admin/users`)
      .then(r => setUsers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Lazy-load the resource catalogue the first time the resource modal opens.
  useEffect(() => {
    if (resourceModalFor && !resourceCatalogue) {
      axios.get(`${API_URL}/admin/resource-catalogue`)
        .then(r => setResourceCatalogue(r.data))
        .catch(() => setResourceCatalogue([]));
    }
  }, [resourceModalFor, resourceCatalogue]);

  // Close categories popover on outside click
  useEffect(() => {
    if (!openCategoriesFor) return;
    const handler = (e) => {
      if (!e.target.closest('[data-categories-popover]') && !e.target.closest('[data-categories-trigger]')) {
        setOpenCategoriesFor(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openCategoriesFor]);

  if (loading) return <Loading />;
  if (users.length === 0) {
    return <EmptyState icon={Users2} title="No users yet" subtitle="Users appear after they sign up via a link or you create a demo account." />;
  }

  const totalResources = resourceCatalogue
    ? resourceCatalogue.reduce((s, p) => s + (p.resources?.length || 0), 0)
    : 0;

  const toggleProgram = async (u) => {
    const next = !u.programs_enabled;
    try {
      await axios.patch(`${API_URL}/auth/teachers/${u._id}/entitlements`, { programs_enabled: next });
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, programs_enabled: next } : x));
    } catch { /* noop */ }
  };

  const toggleAccountType = async (u) => {
    const next = (u.account_type === 'private_center') ? 'school' : 'private_center';
    try {
      await axios.patch(`${API_URL}/auth/teachers/${u._id}/entitlements`, { account_type: next });
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, account_type: next } : x));
    } catch { /* noop */ }
  };

  const toggleCategory = async (u, audienceType) => {
    const current = Array.isArray(u.allowed_audience_types) ? u.allowed_audience_types : [];
    // Empty list means "all allowed". When admin toggles one off from "all",
    // we expand to the explicit full list minus that one.
    let next;
    if (current.length === 0) {
      // Was "all" — switch to explicit list excluding the unchecked one
      next = AUDIENCE_CATEGORIES.map(c => c.key).filter(k => k !== audienceType);
    } else if (current.includes(audienceType)) {
      next = current.filter(k => k !== audienceType);
    } else {
      next = [...current, audienceType];
      // If toggling on completes the full set, collapse back to []  (= all)
      if (next.length === AUDIENCE_CATEGORIES.length) next = [];
    }

    // Edge case: every category unchecked. Auto-flip the master Programs toggle OFF
    // (hides the sidebar item entirely for this org) and reset the allow-list to []
    // so that when the admin re-enables Programs, the default "all categories" returns.
    if (next.length === 0 && current.length > 0) {
      try {
        await axios.patch(`${API_URL}/auth/teachers/${u._id}/entitlements`, {
          programs_enabled: false,
          allowed_audience_types: [],
        });
        setUsers(prev => prev.map(x => x._id === u._id ? { ...x, programs_enabled: false, allowed_audience_types: [] } : x));
        setOpenCategoriesFor(null);
      } catch { /* noop */ }
      return;
    }

    try {
      await axios.patch(`${API_URL}/auth/teachers/${u._id}/entitlements`, { allowed_audience_types: next });
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, allowed_audience_types: next } : x));
    } catch { /* noop */ }
  };

  const toggleResourcesMaster = async (u) => {
    const next = u.resources_enabled === false ? true : false;
    try {
      await axios.patch(`${API_URL}/auth/teachers/${u._id}/entitlements`, { resources_enabled: next });
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, resources_enabled: next } : x));
    } catch { /* noop */ }
  };

  const toggleSingleResource = async (u, resourceId) => {
    const denied = new Set(Array.isArray(u.denied_resource_ids) ? u.denied_resource_ids : []);
    if (denied.has(resourceId)) denied.delete(resourceId);
    else denied.add(resourceId);
    const nextDenied = Array.from(denied).sort();

    // If admin denies EVERY resource in the catalogue, auto-flip the master Resources
    // switch OFF and reset the deny-list (so when they re-enable, default is "all visible").
    if (totalResources > 0 && nextDenied.length >= totalResources) {
      try {
        await axios.patch(`${API_URL}/auth/teachers/${u._id}/entitlements`, {
          resources_enabled: false,
          denied_resource_ids: [],
        });
        setUsers(prev => prev.map(x => x._id === u._id ? { ...x, resources_enabled: false, denied_resource_ids: [] } : x));
        setResourceModalFor(null);
      } catch { /* noop */ }
      return;
    }

    try {
      await axios.patch(`${API_URL}/auth/teachers/${u._id}/entitlements`, { denied_resource_ids: nextDenied });
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, denied_resource_ids: nextDenied } : x));
    } catch { /* noop */ }
  };

  const toggleProgramResources = async (u, programId) => {
    // Bulk: if all resources in this program are currently visible, deny them all; otherwise allow them all.
    if (!resourceCatalogue) return;
    const prog = resourceCatalogue.find(p => p.program_id === programId);
    if (!prog) return;
    const progIds = prog.resources.map(r => r.resource_id);
    const denied = new Set(Array.isArray(u.denied_resource_ids) ? u.denied_resource_ids : []);
    const allDenied = progIds.every(id => denied.has(id));
    if (allDenied) {
      progIds.forEach(id => denied.delete(id));
    } else {
      progIds.forEach(id => denied.add(id));
    }
    const nextDenied = Array.from(denied).sort();
    if (totalResources > 0 && nextDenied.length >= totalResources) {
      try {
        await axios.patch(`${API_URL}/auth/teachers/${u._id}/entitlements`, {
          resources_enabled: false,
          denied_resource_ids: [],
        });
        setUsers(prev => prev.map(x => x._id === u._id ? { ...x, resources_enabled: false, denied_resource_ids: [] } : x));
        setResourceModalFor(null);
      } catch { /* noop */ }
      return;
    }
    try {
      await axios.patch(`${API_URL}/auth/teachers/${u._id}/entitlements`, { denied_resource_ids: nextDenied });
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, denied_resource_ids: nextDenied } : x));
    } catch { /* noop */ }
  };

  return (
    <div data-testid="all-users-tab" style={{
      background: 'white', borderRadius: '0.75rem', border: '1px solid #E2E8F0', overflow: 'visible',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #E2E8F0', background: '#F8F9FB' }}>
            {['Name', 'Institution', 'Role', 'Status', 'Type', 'Programs', 'Resources', 'Sessions', 'Last Active'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '0.625rem 0.875rem', fontWeight: 700, color: '#64748B', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => (
            <tr key={u._id} data-testid={`user-row-${i}`} style={{ borderBottom: '1px solid #F1F5F9' }}>
              <td style={{ padding: '0.625rem 0.875rem' }}>
                <div style={{ fontWeight: 600, color: '#1A1D20' }}>{u.name || '—'}</div>
                <div style={{ fontSize: '0.72rem', color: '#94A3B8' }}>{u.email}</div>
              </td>
              <td style={{ padding: '0.625rem 0.875rem', color: '#475569' }}>{u.school_name || '—'}</td>
              <td style={{ padding: '0.625rem 0.875rem' }}>
                <span style={{ ...badge, background: u.is_demo ? '#FEF3C7' : (u.role === 'center_admin' ? '#E0F2FE' : '#F1F5F9'), color: u.is_demo ? '#92400E' : (u.role === 'center_admin' ? '#0369A1' : '#64748B') }}>
                  {u.is_demo ? 'Demo' : (u.role === 'center_admin' ? 'Center admin' : 'Teacher')}
                </span>
              </td>
              <td style={{ padding: '0.625rem 0.875rem' }}>
                <span style={{ ...badge, background: statusBg(u.access_status), color: statusColor(u.access_status) }}>{statusLabel(u)}</span>
              </td>
              <td style={{ padding: '0.625rem 0.875rem' }}>
                <button
                  data-testid={`row-account-type-${u._id}`}
                  onClick={() => toggleAccountType(u)}
                  title="Click to flip between School and Private Center"
                  style={{
                    ...badge,
                    background: u.account_type === 'private_center' ? '#FEF3C7' : '#DBEAFE',
                    color: u.account_type === 'private_center' ? '#92400E' : '#1E3A8A',
                    border: 'none', cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
                  }}
                >
                  {u.account_type === 'private_center' ? 'Private Center' : 'School'}
                </button>
              </td>
              <td style={{ padding: '0.625rem 0.875rem', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <button data-testid={`row-programs-toggle-${u._id}`} onClick={() => toggleProgram(u)} style={programsPill(u.programs_enabled)}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: u.programs_enabled ? '#2F9E44' : '#CBD5E1' }} />
                    {u.programs_enabled ? 'On' : 'Off'}
                  </button>
                  {u.programs_enabled && (() => {
                    const allowed = Array.isArray(u.allowed_audience_types) ? u.allowed_audience_types : [];
                    const count = allowed.length === 0 ? AUDIENCE_CATEGORIES.length : allowed.length;
                    const isAll = allowed.length === 0;
                    return (
                      <button
                        data-categories-trigger
                        data-testid={`row-categories-trigger-${u._id}`}
                        onClick={() => setOpenCategoriesFor(openCategoriesFor === u._id ? null : u._id)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                          padding: '0.25rem 0.5rem', borderRadius: '999px',
                          border: '1px solid #E2E8F0', background: openCategoriesFor === u._id ? '#F1F5F9' : 'white',
                          fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', fontWeight: 700,
                          color: '#475569', cursor: 'pointer',
                        }}
                      >
                        {isAll ? 'All 5' : `${count}/${AUDIENCE_CATEGORIES.length}`}
                        <span style={{ fontSize: '0.6rem' }}>▾</span>
                      </button>
                    );
                  })()}
                </div>
                {openCategoriesFor === u._id && (
                  <div
                    data-categories-popover
                    data-testid={`categories-popover-${u._id}`}
                    style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: '0.875rem',
                      background: 'white', borderRadius: '0.625rem',
                      border: '1px solid #E2E8F0', boxShadow: '0 12px 32px rgba(15, 23, 42, 0.14)',
                      zIndex: 30, minWidth: 230, padding: '0.5rem',
                    }}
                  >
                    <div style={{ padding: '0.375rem 0.5rem 0.5rem', borderBottom: '1px solid #F1F5F9', marginBottom: '0.25rem' }}>
                      <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.78rem', fontWeight: 700, color: '#1A1D20' }}>
                        Visible categories
                      </div>
                      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', color: '#94A3B8', marginTop: 1 }}>
                        Unchecked = hidden from this org
                      </div>
                    </div>
                    {AUDIENCE_CATEGORIES.map(cat => {
                      const allowed = Array.isArray(u.allowed_audience_types) ? u.allowed_audience_types : [];
                      const checked = allowed.length === 0 || allowed.includes(cat.key);
                      return (
                        <label
                          key={cat.key}
                          data-testid={`category-row-${u._id}-${cat.key}`}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.4rem 0.5rem', borderRadius: '0.375rem',
                            fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#1A1D20',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F8F9FB'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <input
                            type="checkbox"
                            data-testid={`category-cb-${u._id}-${cat.key}`}
                            checked={checked}
                            onChange={() => toggleCategory(u, cat.key)}
                            style={{ width: 14, height: 14, accentColor: '#1E2140', cursor: 'pointer' }}
                          />
                          {cat.label}
                        </label>
                      );
                    })}
                  </div>
                )}
              </td>
              <td style={{ padding: '0.625rem 0.875rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <button
                    data-testid={`row-resources-toggle-${u._id}`}
                    onClick={() => toggleResourcesMaster(u)}
                    style={programsPill(u.resources_enabled !== false)}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: u.resources_enabled !== false ? '#2F9E44' : '#CBD5E1' }} />
                    {u.resources_enabled !== false ? 'On' : 'Off'}
                  </button>
                  {u.resources_enabled !== false && (() => {
                    const denied = Array.isArray(u.denied_resource_ids) ? u.denied_resource_ids.length : 0;
                    const total = totalResources || 129;
                    const visible = total - denied;
                    return (
                      <button
                        data-testid={`row-resources-trigger-${u._id}`}
                        onClick={() => setResourceModalFor(u._id)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                          padding: '0.25rem 0.5rem', borderRadius: '999px',
                          border: '1px solid #E2E8F0', background: 'white',
                          fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', fontWeight: 700,
                          color: '#475569', cursor: 'pointer',
                        }}
                      >
                        {denied === 0 ? `All ${total}` : `${visible}/${total}`}
                        <span style={{ fontSize: '0.6rem' }}>▾</span>
                      </button>
                    );
                  })()}
                </div>
              </td>
              <td style={{ padding: '0.625rem 0.875rem' }}>
                <span style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: '#1A1D20' }}>{u.total_sessions}</span>
                {u.unique_games > 0 && <span style={{ color: '#94A3B8', fontSize: '0.72rem', marginLeft: '0.25rem' }}>({u.unique_games}g)</span>}
              </td>
              <td style={{ padding: '0.625rem 0.875rem', color: u.last_active ? '#475569' : '#CBD5E1' }}>
                {u.last_active ? new Date(u.last_active).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {resourceModalFor && (() => {
        const u = users.find(x => x._id === resourceModalFor);
        if (!u) return null;
        const denied = new Set(Array.isArray(u.denied_resource_ids) ? u.denied_resource_ids : []);
        return (
          <div
            data-testid={`resource-modal-${u._id}`}
            onClick={(e) => { if (e.target === e.currentTarget) setResourceModalFor(null); }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 200, padding: '2rem',
            }}
          >
            <div
              style={{
                background: 'white', borderRadius: '1rem', width: '100%', maxWidth: 720,
                maxHeight: '80vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 24px 64px rgba(15, 23, 42, 0.35)',
              }}
            >
              {/* Header */}
              <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem', fontWeight: 700, color: '#1A1D20' }}>
                    Resource access for {u.name || u.email}
                  </div>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', color: '#64748B', marginTop: 2 }}>
                    Uncheck any resource to hide it from this org. Uncheck every resource to turn the Resources tab off entirely.
                  </div>
                </div>
                <button
                  data-testid={`resource-modal-close-${u._id}`}
                  onClick={() => setResourceModalFor(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4 }}
                >
                  <X size={20} />
                </button>
              </div>
              {/* Body */}
              <div style={{ flex: 1, overflow: 'auto', padding: '0.75rem 1.5rem' }}>
                {!resourceCatalogue && (
                  <div style={{ padding: '2rem 0', textAlign: 'center', color: '#94A3B8', fontFamily: 'Nunito, sans-serif' }}>
                    Loading catalogue...
                  </div>
                )}
                {resourceCatalogue && resourceCatalogue.filter(p => !p.hidden).map(prog => {
                  const progIds = prog.resources.map(r => r.resource_id);
                  const deniedInProg = progIds.filter(id => denied.has(id)).length;
                  const allDenied = progIds.length > 0 && deniedInProg === progIds.length;
                  return (
                    <div key={prog.program_id} style={{ marginBottom: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #F1F5F9', marginBottom: '0.25rem' }}>
                        <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.88rem', fontWeight: 700, color: '#1A1D20' }}>
                          {prog.title}
                          <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', fontWeight: 600, color: '#94A3B8', marginLeft: '0.5rem' }}>
                            {progIds.length - deniedInProg}/{progIds.length}
                          </span>
                        </div>
                        <button
                          data-testid={`resource-bulk-${u._id}-${prog.program_id}`}
                          onClick={() => toggleProgramResources(u, prog.program_id)}
                          style={{
                            background: 'none', border: '1px solid #E2E8F0', borderRadius: '0.375rem',
                            padding: '0.2rem 0.5rem', cursor: 'pointer',
                            fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', fontWeight: 600,
                            color: '#475569',
                          }}
                        >
                          {allDenied ? 'Enable all' : 'Disable all'}
                        </button>
                      </div>
                      {prog.resources.map(res => {
                        const isDenied = denied.has(res.resource_id);
                        return (
                          <label
                            key={res.resource_id}
                            data-testid={`resource-row-${u._id}-${res.resource_id}`}
                            style={{
                              display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
                              padding: '0.45rem 0.5rem', borderRadius: '0.375rem',
                              cursor: 'pointer',
                              fontFamily: 'Nunito, sans-serif',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F8F9FB'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <input
                              type="checkbox"
                              data-testid={`resource-cb-${u._id}-${res.resource_id}`}
                              checked={!isDenied}
                              onChange={() => toggleSingleResource(u, res.resource_id)}
                              style={{ width: 14, height: 14, accentColor: '#1E2140', cursor: 'pointer', marginTop: 3 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '0.83rem', fontWeight: 600, color: '#1A1D20' }}>{res.title}</div>
                              <div style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: 1 }}>
                                {res.type}{res.audience ? ` · ${res.audience}` : ''}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              {/* Footer */}
              <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#64748B' }}>
                  {(() => {
                    const visible = totalResources - denied.size;
                    return `${visible} of ${totalResources} resources visible to this org`;
                  })()}
                </div>
                <button
                  data-testid={`resource-modal-done-${u._id}`}
                  onClick={() => setResourceModalFor(null)}
                  style={{
                    padding: '0.5rem 1.25rem', borderRadius: '0.5rem',
                    background: '#1E2140', color: 'white', border: 'none',
                    fontFamily: 'Outfit, sans-serif', fontSize: '0.82rem', fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ================================================================ */
/*                        Shared helpers                              */
/* ================================================================ */

function Field({ label, children }) {
  return (
    <div>
      <label style={smallLabel}>{label}</label>
      {children}
    </div>
  );
}

function PillRow({ label, value, onChange, options, testIdPrefix }) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={smallLabel}>{label}</label>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.375rem' }}>
        {options.map(o => (
          <button key={o.v} type="button" onClick={() => onChange(o.v)} data-testid={`${testIdPrefix}-${o.v}`} style={pill(value === o.v)}>
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'Nunito, sans-serif', color: '#94A3B8', fontSize: '0.9rem' }}>
      Loading...
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div style={{
      padding: '3rem 2rem', textAlign: 'center', background: 'white',
      borderRadius: '0.75rem', border: '1px dashed #E2E8F0',
    }}>
      <Icon size={32} color="#CBD5E1" style={{ marginBottom: '0.75rem' }} />
      <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1A1D20', marginBottom: '0.375rem' }}>{title}</div>
      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B' }}>{subtitle}</div>
    </div>
  );
}

const statusLabel = (u) => {
  if (u.access_status === 'demo') return `Demo (${u.demo_days_remaining}d)`;
  if (u.access_status === 'demo_expired') return 'Demo expired';
  if (u.access_status === 'trial') return `Trial (${u.trial_days_remaining}d)`;
  if (u.access_status === 'trial_expired') return 'Trial expired';
  if (u.access_status === 'subscribed') return 'Subscribed';
  return 'Active';
};

const statusBg = (s) => {
  if (s === 'demo') return '#FEF3C7';
  if (s === 'demo_expired' || s === 'trial_expired') return '#FEF2F2';
  if (s === 'trial') return '#EDF2FF';
  if (s === 'subscribed') return '#EBFBEE';
  return '#F0FDF4';
};
const statusColor = (s) => {
  if (s === 'demo') return '#92400E';
  if (s === 'demo_expired' || s === 'trial_expired') return '#DC2626';
  if (s === 'trial') return '#4C6EF5';
  if (s === 'subscribed') return '#2F9E44';
  return '#16A34A';
};

const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
  padding: '0.625rem 1.125rem', borderRadius: '0.5rem',
  background: '#1E2140', color: 'white', border: 'none',
  fontFamily: 'Outfit, sans-serif', fontSize: '0.85rem', fontWeight: 600,
  cursor: 'pointer',
};

const iconBtn = {
  background: 'transparent', border: '1px solid #E2E8F0', borderRadius: '0.4rem',
  padding: '0.4rem', cursor: 'pointer', color: '#64748B',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

const inputStyle = {
  width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
  border: '1.5px solid #E2E8F0', fontFamily: 'Nunito, sans-serif',
  fontSize: '0.875rem', outline: 'none', background: 'white', color: '#1A1D20',
};

const smallLabel = {
  display: 'block', fontFamily: 'Nunito, sans-serif',
  fontSize: '0.75rem', fontWeight: 600, color: '#475569',
  marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.04em',
};

const pill = (active) => ({
  padding: '0.4rem 0.875rem', borderRadius: '1rem',
  border: active ? '2px solid #1E2140' : '1.5px solid #E2E8F0',
  background: active ? '#1E2140' : 'white',
  color: active ? 'white' : '#475569',
  fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', fontWeight: 600,
  cursor: 'pointer', transition: 'all 0.15s',
});

const badge = {
  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
  padding: '0.15rem 0.5rem', borderRadius: '1rem',
  fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', fontWeight: 700,
  letterSpacing: '0.02em',
};

const programsPill = (on) => ({
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
  padding: '0.2rem 0.6rem', borderRadius: '1rem',
  background: on ? '#EBFBEE' : '#F1F5F9',
  color: on ? '#2F9E44' : '#94A3B8',
  border: on ? '1px solid #2F9E44' : '1px solid #E2E8F0',
  fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
});

/* ================================================================ */
/*                          API Keys                                  */
/* ================================================================ */

function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingKey, setEditingKey] = useState(null); // key whose allowlist is being edited
  const [justCreated, setJustCreated] = useState(null); // { secret, prefix, name } shown once
  const [copiedField, setCopiedField] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/api-keys`);
      setKeys(data);
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 1500);
    } catch { /* noop */ }
  };

  const toggleActive = async (k) => {
    try {
      await axios.patch(`${API_URL}/admin/api-keys/${k.id}`, { is_active: !k.is_active });
      load();
    } catch { /* noop */ }
  };

  const remove = async (k) => {
    if (!window.confirm(`Delete API key "${k.name}"? Any agent or script using it will lose access immediately.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/api-keys/${k.id}`);
      load();
    } catch { /* noop */ }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div data-testid="api-keys-tab">
      <div style={{
        background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0',
        padding: '1.5rem', marginBottom: '1.25rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem', fontWeight: 600, color: '#1A1D20', margin: 0, marginBottom: '0.375rem' }}>
              API Keys
            </h3>
            <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', margin: 0, lineHeight: 1.6 }}>
              Long-lived secrets for machine-to-machine access. Use them from a script
              or local agent to spin up invite links, pull visitor intel, and run any
              Platform Admin action without juggling cookies. Keys never expire — rotate
              by minting a new one and deleting the old.
            </p>
          </div>
          <button
            data-testid="api-key-create-button"
            onClick={() => setShowCreate(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.625rem 1rem', background: '#1E2140',
              color: 'white', border: 'none', borderRadius: '0.625rem',
              fontFamily: 'Outfit, sans-serif', fontSize: '0.85rem', fontWeight: 600,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            <Plus size={15} /> New key
          </button>
        </div>

        <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#F8F9FB', borderRadius: '0.5rem', border: '1px solid #E2E8F0' }}>
          <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', color: '#475569', lineHeight: 1.6 }}>
            <strong style={{ color: '#1A1D20' }}>Connection details for your agent:</strong>
            <div style={{ marginTop: '0.5rem', display: 'grid', gridTemplateColumns: '120px 1fr auto', gap: '0.5rem 0.75rem', alignItems: 'center' }}>
              <span style={{ color: '#64748B' }}>Base URL</span>
              <code style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#1E2140', background: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid #E2E8F0' }}>{baseUrl}</code>
              <button
                onClick={() => copy(baseUrl, 'base')}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748B', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem' }}
              >
                {copiedField === 'base' ? <Check size={13} color="#2F9E44" /> : <Copy size={13} />}
              </button>

              <span style={{ color: '#64748B' }}>Auth header</span>
              <code style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#1E2140', background: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid #E2E8F0' }}>X-API-Key: jdf_live_…</code>
              <span />

              <span style={{ color: '#64748B' }}>Docs endpoint</span>
              <code style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#1E2140', background: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid #E2E8F0' }}>GET /api/admin/api-keys/docs</code>
              <button
                onClick={() => copy(`${baseUrl}/api/admin/api-keys/docs`, 'docs')}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748B', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem' }}
              >
                {copiedField === 'docs' ? <Check size={13} color="#2F9E44" /> : <Copy size={13} />}
              </button>
            </div>
            <div style={{ marginTop: '0.6rem', fontSize: '0.75rem', color: '#64748B' }}>
              The agent can fetch the docs endpoint with its key for the full API reference
              (every invite-link endpoint plus users / organizations / activity).
            </div>
          </div>
        </div>
      </div>

      {/* Just-created secret banner */}
      {justCreated && (
        <div data-testid="api-key-just-created" style={{
          background: '#FFFBEB', border: '2px solid #FBBF24', borderRadius: '0.875rem',
          padding: '1.25rem', marginBottom: '1.25rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                <AlertTriangle size={16} color="#D97706" />
                <strong style={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.95rem', color: '#92400E' }}>
                  Copy this key now. It will not be shown again.
                </strong>
              </div>
              <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.82rem', color: '#92400E', marginBottom: '0.75rem' }}>
                Paste it into your agent. Once you close this banner the full secret is gone — only the prefix remains.
              </div>
            </div>
            <button
              data-testid="api-key-dismiss-banner"
              onClick={() => setJustCreated(null)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#92400E' }}
            >
              <X size={18} />
            </button>
          </div>

          <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: 'white', borderRadius: '0.5rem', border: '1px solid #FCD34D' }}>
            <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#1A1D20', wordBreak: 'break-all', marginBottom: '0.5rem' }}>
              {justCreated.secret}
            </div>
            <button
              data-testid="api-key-copy-secret"
              onClick={() => copy(justCreated.secret, 'secret')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.4rem 0.8rem', background: '#1E2140', color: 'white',
                border: 'none', borderRadius: '0.4rem',
                fontFamily: 'Outfit, sans-serif', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {copiedField === 'secret' ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy full secret</>}
            </button>
          </div>

          <details style={{ marginTop: '0.75rem' }}>
            <summary style={{ cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', color: '#92400E', fontWeight: 600 }}>
              Show what to paste to your local agent ▾
            </summary>
            <pre style={{
              marginTop: '0.5rem', padding: '0.75rem', background: '#1E2140', color: '#E2E8F0',
              borderRadius: '0.5rem', fontSize: '0.72rem', overflow: 'auto', lineHeight: 1.5,
            }}>
{`# Connect to FrenchBoard Platform Admin
BASE_URL = "${baseUrl}"
API_KEY  = "${justCreated.secret}"

# Spin up 10 invite links with a label
curl -s -X POST "$BASE_URL/api/admin/invites" \\
  -H "X-API-Key: $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"count": 10, "label": "BC private schools batch", "default_duration_days": 30}'

# Fetch the full reference
curl -s "$BASE_URL/api/admin/api-keys/docs" \\
  -H "X-API-Key: $API_KEY" | jq -r .content`}
            </pre>
          </details>
        </div>
      )}

      {/* Key list */}
      <div style={{ background: 'white', borderRadius: '0.875rem', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#94A3B8', fontFamily: 'Nunito, sans-serif' }}>Loading…</div>
        ) : keys.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
            <Key size={28} color="#CBD5E1" style={{ marginBottom: '0.75rem' }} />
            <div style={{ fontSize: '0.95rem', color: '#475569', fontWeight: 600 }}>No API keys yet</div>
            <div style={{ fontSize: '0.82rem', color: '#94A3B8', marginTop: '0.25rem' }}>
              Create one to let your local agent connect.
            </div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8F9FB', borderBottom: '1px solid #E2E8F0' }}>
                {['Name', 'Prefix', 'Status', 'IP Lock', 'Last used', 'IP', 'Calls', 'Created', ''].map(h => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '0.6rem 0.9rem',
                    fontFamily: 'Outfit, sans-serif', fontSize: '0.72rem',
                    fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map(k => {
                const ipCount = (k.allowed_ips || []).length;
                const blocked = k.blocked_count || 0;
                return (
                <tr key={k.id} data-testid={`api-key-row-${k.id}`} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '0.7rem 0.9rem', fontFamily: 'Nunito, sans-serif', fontSize: '0.86rem', color: '#1A1D20', fontWeight: 600 }}>
                    {k.name}
                  </td>
                  <td style={{ padding: '0.7rem 0.9rem', fontFamily: 'monospace', fontSize: '0.78rem', color: '#475569' }}>
                    {k.prefix}…
                  </td>
                  <td style={{ padding: '0.7rem 0.9rem' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.2rem 0.55rem', borderRadius: '1rem',
                      background: k.is_active ? '#EBFBEE' : '#FEE2E2',
                      color: k.is_active ? '#2F9E44' : '#B91C1C',
                      fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', fontWeight: 700,
                    }}>
                      {k.is_active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td style={{ padding: '0.7rem 0.9rem' }}>
                    <button
                      data-testid={`api-key-edit-ips-${k.id}`}
                      onClick={() => setEditingKey(k)}
                      title={ipCount === 0 ? 'No IP restriction — click to add' : `${ipCount} entry(ies)`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                        padding: '0.2rem 0.5rem', borderRadius: '1rem',
                        background: ipCount > 0 ? '#FEF3C7' : '#F1F5F9',
                        color: ipCount > 0 ? '#92400E' : '#64748B',
                        border: ipCount > 0 ? '1px solid #FBBF24' : '1px solid #E2E8F0',
                        fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      {ipCount > 0
                        ? <><Lock size={11} /> {ipCount} IP{ipCount > 1 ? 's' : ''}</>
                        : <><Unlock size={11} /> Open</>}
                    </button>
                    {blocked > 0 && (
                      <div style={{ marginTop: '0.2rem', fontSize: '0.68rem', color: '#B91C1C', fontFamily: 'Nunito, sans-serif' }}>
                        {blocked} blocked
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.7rem 0.9rem', fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', color: '#475569' }}>
                    {fmt(k.last_used_at)}
                  </td>
                  <td style={{ padding: '0.7rem 0.9rem', fontFamily: 'monospace', fontSize: '0.74rem', color: '#64748B' }}>
                    {k.last_used_ip || '—'}
                  </td>
                  <td style={{ padding: '0.7rem 0.9rem', fontFamily: 'Nunito, sans-serif', fontSize: '0.82rem', color: '#1A1D20', fontWeight: 600 }}>
                    {k.use_count}
                  </td>
                  <td style={{ padding: '0.7rem 0.9rem', fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', color: '#64748B' }}>
                    {fmt(k.created_at)}
                  </td>
                  <td style={{ padding: '0.7rem 0.9rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      data-testid={`api-key-toggle-${k.id}`}
                      onClick={() => toggleActive(k)}
                      title={k.is_active ? 'Revoke' : 'Reactivate'}
                      style={{
                        background: 'transparent', border: '1px solid #E2E8F0', borderRadius: '0.375rem',
                        padding: '0.3rem 0.5rem', cursor: 'pointer',
                        color: k.is_active ? '#D97706' : '#2F9E44', marginRight: '0.4rem',
                      }}
                    >
                      <Power size={13} />
                    </button>
                    <button
                      data-testid={`api-key-delete-${k.id}`}
                      onClick={() => remove(k)}
                      title="Delete"
                      style={{
                        background: 'transparent', border: '1px solid #FECACA', borderRadius: '0.375rem',
                        padding: '0.3rem 0.5rem', cursor: 'pointer', color: '#B91C1C',
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateApiKeyDialog
          onClose={() => setShowCreate(false)}
          onCreated={(k) => { setShowCreate(false); setJustCreated(k); load(); }}
        />
      )}
      {editingKey && (
        <EditAllowlistDialog
          k={editingKey}
          onClose={() => setEditingKey(null)}
          onSaved={() => { setEditingKey(null); load(); }}
        />
      )}
    </div>
  );
}

function CreateApiKeyDialog({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [allowedIps, setAllowedIps] = useState(''); // comma- or newline-separated
  const [myIp, setMyIp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Fetch the admin's current IP so they can paste it in with one click
  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/admin/api-keys/whoami`)
      .then(({ data }) => { if (alive) setMyIp(data?.client_ip || ''); })
      .catch(() => { /* noop */ });
    return () => { alive = false; };
  }, []);

  const parseIps = (raw) =>
    raw.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const payload = { name: name.trim() || 'API Key' };
      const ips = parseIps(allowedIps);
      if (ips.length) payload.allowed_ips = ips;
      const { data } = await axios.post(`${API_URL}/admin/api-keys`, payload);
      onCreated(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not create key. Try again.');
      setBusy(false);
    }
  };

  const insertMyIp = () => {
    if (!myIp) return;
    const cur = parseIps(allowedIps);
    if (cur.includes(myIp)) return;
    setAllowedIps([...cur, myIp].join('\n'));
  };

  return (
    <div
      data-testid="api-key-create-dialog"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'white', borderRadius: '0.875rem', padding: '1.5rem', width: '100%', maxWidth: 540,
        maxHeight: '90vh', overflow: 'auto',
      }}>
        <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: '#1A1D20', margin: 0, marginBottom: '0.375rem' }}>
          Create new API key
        </h3>
        <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.82rem', color: '#64748B', margin: 0, marginBottom: '1rem' }}>
          Give the key a name so you remember what's using it (e.g. "Mac local agent", "Outreach script").
        </p>

        <label style={{ display: 'block', fontFamily: 'Outfit, sans-serif', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '0.4rem' }}>
          Name
        </label>
        <input
          data-testid="api-key-name-input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          placeholder="Mac local agent"
          style={{
            width: '100%', padding: '0.55rem 0.75rem',
            border: '1.5px solid #E2E8F0', borderRadius: '0.5rem',
            fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', color: '#1A1D20',
            outline: 'none', boxSizing: 'border-box',
          }}
        />

        <div style={{ marginTop: '1.1rem', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.5rem' }}>
          <label style={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>
            IP allowlist <span style={{ fontWeight: 400, color: '#94A3B8' }}>· optional</span>
          </label>
          {myIp && (
            <button
              type="button"
              data-testid="api-key-add-my-ip"
              onClick={insertMyIp}
              style={{
                background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: '0.35rem',
                padding: '0.2rem 0.55rem', cursor: 'pointer',
                fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', fontWeight: 600, color: '#1E2140',
              }}
            >
              + Add my IP ({myIp})
            </button>
          )}
        </div>
        <textarea
          data-testid="api-key-allowed-ips-input"
          value={allowedIps}
          onChange={(e) => setAllowedIps(e.target.value)}
          placeholder="Leave empty to allow any IP (recommended if you use a VPN with rotating IPs).&#10;&#10;One per line. Supports CIDR:&#10;1.2.3.4&#10;10.0.0.0/24"
          rows={4}
          style={{
            width: '100%', padding: '0.55rem 0.75rem', marginTop: '0.4rem',
            border: '1.5px solid #E2E8F0', borderRadius: '0.5rem',
            fontFamily: 'monospace', fontSize: '0.82rem', color: '#1A1D20',
            outline: 'none', boxSizing: 'border-box', resize: 'vertical',
          }}
        />
        <div style={{ marginTop: '0.4rem', fontFamily: 'Nunito, sans-serif', fontSize: '0.74rem', color: '#64748B', lineHeight: 1.5 }}>
          Empty = key works from any IP (the random 256-bit secret is the credential).
          If you have a VPN that hands you a known CIDR (e.g. <code>185.236.0.0/16</code>), paste that.
          Bad guesses block you with a clear 401 — you can come back and edit any time.
        </div>

        {error && (
          <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: '#FEF2F2', color: '#B91C1C', borderRadius: '0.4rem', fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.55rem 1rem', background: 'transparent', color: '#475569',
              border: '1px solid #E2E8F0', borderRadius: '0.5rem',
              fontFamily: 'Outfit, sans-serif', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            data-testid="api-key-create-submit"
            onClick={submit}
            disabled={busy}
            style={{
              padding: '0.55rem 1rem', background: '#1E2140', color: 'white',
              border: 'none', borderRadius: '0.5rem',
              fontFamily: 'Outfit, sans-serif', fontSize: '0.85rem', fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? 'Creating…' : 'Create key'}
          </button>
        </div>
      </div>
    </div>
  );
}


function EditAllowlistDialog({ k, onClose, onSaved }) {
  const [text, setText] = useState((k.allowed_ips || []).join('\n'));
  const [myIp, setMyIp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/admin/api-keys/whoami`)
      .then(({ data }) => { if (alive) setMyIp(data?.client_ip || ''); })
      .catch(() => { /* noop */ });
    return () => { alive = false; };
  }, []);

  const parseIps = (raw) => raw.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);

  const save = async () => {
    setBusy(true); setError('');
    try {
      await axios.patch(`${API_URL}/admin/api-keys/${k.id}`, { allowed_ips: parseIps(text) });
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save. Try again.');
      setBusy(false);
    }
  };

  const insertMyIp = () => {
    if (!myIp) return;
    const cur = parseIps(text);
    if (cur.includes(myIp)) return;
    setText([...cur, myIp].join('\n'));
  };

  return (
    <div
      data-testid="api-key-edit-ips-dialog"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'white', borderRadius: '0.875rem', padding: '1.5rem', width: '100%', maxWidth: 540,
        maxHeight: '90vh', overflow: 'auto',
      }}>
        <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem', fontWeight: 700, color: '#1A1D20', margin: 0, marginBottom: '0.375rem' }}>
          IP allowlist · <span style={{ color: '#64748B', fontWeight: 500 }}>{k.name}</span>
        </h3>
        <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.82rem', color: '#64748B', margin: 0, marginBottom: '1rem' }}>
          Empty = key works from any IP (relies on the secret only). Add specific IPs or
          CIDR ranges to lock it down. Blocked attempts are counted on this key — check the table.
        </p>

        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.5rem' }}>
          <label style={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>
            Allowed IPs / CIDRs
          </label>
          {myIp && (
            <button
              type="button"
              onClick={insertMyIp}
              style={{
                background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: '0.35rem',
                padding: '0.2rem 0.55rem', cursor: 'pointer',
                fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', fontWeight: 600, color: '#1E2140',
              }}
            >
              + Add my IP ({myIp})
            </button>
          )}
        </div>
        <textarea
          data-testid="api-key-edit-ips-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="1.2.3.4&#10;10.0.0.0/24"
          rows={5}
          style={{
            width: '100%', padding: '0.55rem 0.75rem', marginTop: '0.4rem',
            border: '1.5px solid #E2E8F0', borderRadius: '0.5rem',
            fontFamily: 'monospace', fontSize: '0.82rem', color: '#1A1D20',
            outline: 'none', boxSizing: 'border-box', resize: 'vertical',
          }}
        />

        {error && (
          <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: '#FEF2F2', color: '#B91C1C', borderRadius: '0.4rem', fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button
            onClick={() => setText('')}
            type="button"
            style={{
              padding: '0.55rem 1rem', background: 'transparent', color: '#B91C1C',
              border: '1px solid #FECACA', borderRadius: '0.5rem',
              fontFamily: 'Outfit, sans-serif', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Clear all
          </button>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.55rem 1rem', background: 'transparent', color: '#475569',
                border: '1px solid #E2E8F0', borderRadius: '0.5rem',
                fontFamily: 'Outfit, sans-serif', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              data-testid="api-key-edit-ips-save"
              onClick={save}
              disabled={busy}
              style={{
                padding: '0.55rem 1rem', background: '#1E2140', color: 'white',
                border: 'none', borderRadius: '0.5rem',
                fontFamily: 'Outfit, sans-serif', fontSize: '0.85rem', fontWeight: 600,
                cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
